import { ProductUid, RelatedProductMapItem } from '../domain/models/related-products.models';
import { RelatedProductsCarousel, RelatedProductTypeConfig } from '../infrastructure/state/related-products.state';

import { ProductPublicModels } from '@features/products/product';

// #region PRIVATE
const findProductUidOnProductArray = (
    productArray: ProductPublicModels.PublicProduct[],
    productUid: ProductUid
): ProductPublicModels.PublicProduct => {
    return productArray.find((prod) =>
        prod?.id === productUid?.mocaId
        || prod?.id === productUid?.product_id || prod?.moca === productUid?.uid?.slice(1));
};

const filterItemWithExcludedSubfamily = (
    product: ProductPublicModels.PublicProduct,
    config: RelatedProductTypeConfig
): boolean => {
    return config?.excluded?.subFamilyIds?.includes(product?.subFamily);
};

const filterMaxProducts = (
    allProductsUids: ProductUid[],
    newProducts: Record<string, string>,
    carousel: Partial<RelatedProductsCarousel>,
): ProductUid[] => {
    const maxProductsCarousel = carousel.relatedTypes.reduce((acc, relatedType) => {
        return relatedType.maxItems + acc;
    }, 0);
    const productsLoadedTotal = [...Object.keys(carousel?.productsLoaded), ...Object.keys(newProducts)];

    if (productsLoadedTotal.length >= maxProductsCarousel) {
        return allProductsUids.slice(0, maxProductsCarousel);
    }
    return allProductsUids;
};

const filterRepeatedProducts = (
    newProducts: ProductPublicModels.PublicProduct[],
    currentProducts: Record<string, string>
): ProductPublicModels.PublicProduct[] => {
    const currentProductsIds = Object.values(currentProducts);
    const filteredArray: ProductPublicModels.PublicProduct[] = newProducts.reduce((acc, curr) => {
        const isRepeated = currentProductsIds.includes(curr.id);
        if (!isRepeated) {
            acc.push(curr);
        }
        return acc;
    }, []);

    return filteredArray;
};

// #endregion FILTERS

const getFilteredItemsIds = (
    itemsIds: ProductUid[],
    productsRepeatedFiltered: ProductPublicModels.PublicProduct[],
    carousel: Partial<RelatedProductsCarousel>,
    productsMap: any,
): ProductUid[] => {
    const filteredResponseIds = itemsIds.filter((reqItem) => {
        return !findProductUidOnProductArray(productsRepeatedFiltered, reqItem);
    });
    const filteredItemsIds = carousel.filteredItemsIds.filter((item) => {
        const foundProduct = findProductUidOnProductArray(productsRepeatedFiltered, item);
        const config = carousel.relatedTypes?.find((val) => val.name === item.relatedType);
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        return !filteredResponseIds.includes(item) && !filterItemWithExcludedSubfamily(foundProduct, config);
    });

    return filterMaxProducts(filteredItemsIds, productsMap, carousel);
};

// #endregion PRIVATE

// #region PUBLIC
export const mapResponseProductsTransformer = (
    requestProducts: ProductPublicModels.PublicProduct[][],
    carousel: Partial<RelatedProductsCarousel>,
    requestItemsIds: ProductUid[][],
): {
    productsMap: Record<string, RelatedProductMapItem>;
    filteredItemsIds: ProductUid[];
} => {
    const products = requestProducts.reduce((acc, prods) => acc.concat(prods), []);
    const itemsIds = requestItemsIds.reduce((acc, prods) => acc.concat(prods), []);
    const productsRepeatedFiltered = filterRepeatedProducts(products, carousel?.productsLoaded);

    const productsMap = itemsIds.reduce((acc, reqItem) => {
        const foundProduct = findProductUidOnProductArray(productsRepeatedFiltered, reqItem);
        const config = carousel.relatedTypes?.find((val) => val.name === reqItem.relatedType);
        const excludedFromSubfamily = filterItemWithExcludedSubfamily(foundProduct, config);
        if (foundProduct?.id && !excludedFromSubfamily) {
            acc[reqItem.product_id || reqItem.uid] = {
                id: foundProduct.id,
                relatedType: reqItem.relatedType,
            };
        }
        return acc;
    }, {});

    const filteredItemsIds = getFilteredItemsIds(itemsIds, productsRepeatedFiltered, carousel, productsMap);
    return { productsMap, filteredItemsIds };
};
// #endregion PUBLIC

// #region __TESTS__
export const __tests__ = {
    filterMaxProducts,
};
// #endregion __TESTS__
